import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import "./DataPrivacy.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPES = [
  { key: "all", label: "Everything (recommended)", desc: "Full parish archive as a single ZIP." },
  { key: "parish-profile", label: "Parish Profile", desc: "Identity, worship identity, contacts." },
  { key: "worship-music", label: "Worship & Music Profile", desc: "Hymnals, choir profile, musical forces." },
  { key: "reports", label: "SundayBuilder™ Reports", desc: "Every worship plan you've generated." },
  { key: "calendar", label: "Liturgical Calendar", desc: "Derived from your report history." },
  { key: "hymn-history", label: "Hymn & Anthem History", desc: "Every hymn/anthem recommendation as CSV + JSON." },
  { key: "documents", label: "Uploaded Documents", desc: "Metadata for anything you've uploaded." },
];

const FORMATS = [
  { key: "zip", label: "ZIP (with README + MANIFEST + checksums)" },
  { key: "json", label: "JSON (one combined document)" },
  { key: "csv", label: "CSV (hymn-history scope only)" },
];

function StatusPill({ status }) {
  const map = {
    pending: { bg: "#fdf2d6", fg: "#8a5a00", text: "Preparing" },
    running: { bg: "#e5f0ff", fg: "#1F2A44", text: "Running" },
    ready: { bg: "#e0f2e5", fg: "#2f6f4e", text: "Ready" },
    failed: { bg: "#fde2e2", fg: "#9b1c1c", text: "Failed" },
    expired: { bg: "#eee", fg: "#666", text: "Expired" },
  };
  const s = map[status] || { bg: "#eee", fg: "#666", text: status || "?" };
  return <span className="dp-pill" style={{ background: s.bg, color: s.fg }} data-testid={`dp-status-${status}`}>{s.text}</span>;
}


export default function DataPrivacy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState("all");
  const [fmt, setFmt] = useState("zip");
  const [creating, setCreating] = useState(false);
  const [history, setHistory] = useState([]);
  const [statuses, setStatuses] = useState({}); // job_id -> status doc
  const [correction, setCorrection] = useState({ category: "parish_profile", description: "", affected_record_id: "" });
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [deletion, setDeletion] = useState({ pending: false, request: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteStep, setDeleteStep] = useState(1); // 1=warning, 2=password, 3=token
  const [confirmToken, setConfirmToken] = useState("");
  const [confirming, setConfirming] = useState(false);
  const pollIntervals = useRef({}); // job_id -> setInterval handle

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/data/export/history`);
      setHistory(r.data?.jobs || []);
    } catch {
      setHistory([]);
    }
  }, []);

  const loadDeletionStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/account/deletion/status`);
      setDeletion({ pending: !!r.data?.has_pending_deletion, request: r.data?.request || null });
    } catch {
      setDeletion({ pending: false, request: null });
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadDeletionStatus();
  }, [loadHistory, loadDeletionStatus]);

  const pollStatus = useCallback((jobId) => {
    // Idempotent: if a poll is already active for this job, do nothing.
    if (pollIntervals.current[jobId]) return;
    const stop = () => {
      const h = pollIntervals.current[jobId];
      if (h) {
        clearInterval(h);
        delete pollIntervals.current[jobId];
      }
    };
    const handle = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/data/export/${jobId}/status`);
        setStatuses((prev) => ({ ...prev, [jobId]: r.data }));
        if (r.data?.status === "ready" || r.data?.status === "failed") {
          stop();
          loadHistory();
        }
      } catch {
        stop();
      }
    }, 1500);
    pollIntervals.current[jobId] = handle;
    // Safety timeout after 60s
    setTimeout(stop, 60000);
  }, [loadHistory]);

  // Cleanup on unmount — kill every pending interval.
  useEffect(() => () => {
    Object.values(pollIntervals.current).forEach(clearInterval);
    pollIntervals.current = {};
  }, []);

  const createExport = async (fixedScope, fixedFmt) => {
    setCreating(true);
    try {
      const body = { scope: fixedScope || scope, format: fixedFmt || fmt };
      const r = await axios.post(`${API}/data/export`, body);
      const jobId = r.data?.job_id;
      if (jobId) {
        toast.success("Export started. Preparing your archive…");
        setStatuses((prev) => ({ ...prev, [jobId]: r.data }));
        pollStatus(jobId);
        loadHistory();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start export.");
    } finally {
      setCreating(false);
    }
  };

  const downloadReady = async (jobId) => {
    try {
      // Ask the status endpoint for a fresh short-lived URL.
      const s = await axios.get(`${API}/data/export/${jobId}/status`);
      const url = s.data?.download_url;
      const suggestedName = s.data?.file_name || "export.zip";
      if (!url) {
        const st = s.data?.status;
        toast.error(
          st === "expired" ? "Export file expired. Please create a new export."
          : st === "failed" ? "Export failed. Please try again."
          : "Download link not available yet."
        );
        return;
      }
      const fullUrl = `${API.replace(/\/api$/, "")}${url}`;
      // Fetch with credentials so the cookie session goes along, then force download.
      const resp = await axios.get(fullUrl, { responseType: "blob", withCredentials: true });
      const blob = new Blob([resp.data], {
        type: resp.headers?.["content-type"] || "application/octet-stream",
      });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      // Refresh history so downloaded_at shows up.
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    }
  };

  const submitCorrection = async (e) => {
    e.preventDefault();
    if (correction.description.trim().length < 10) {
      toast.error("Please describe the correction in at least 10 characters.");
      return;
    }
    setSubmittingCorrection(true);
    try {
      await axios.post(`${API}/data/correction-request`, {
        category: correction.category,
        description: correction.description.trim(),
        affected_record_id: correction.affected_record_id || null,
      });
      toast.success("Correction request submitted.");
      setCorrection({ category: "parish_profile", description: "", affected_record_id: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit request.");
    } finally {
      setSubmittingCorrection(false);
    }
  };

  const startDelete = () => {
    setDeleteStep(1);
    setDeletePassword("");
    setDeleteReason("");
    setConfirmToken("");
    setShowDeleteModal(true);
  };

  const requestDeletion = async () => {
    if (!deletePassword) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/account/delete/request`, {
        password: deletePassword, reason: deleteReason || null,
      });
      toast.success("Confirmation sent. Check your email for a token to complete deletion.");
      setDeleteStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start deletion.");
    } finally {
      setConfirming(false);
    }
  };

  const confirmDeletion = async () => {
    if (!confirmToken || !deletePassword) return;
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/account/delete/confirm`, {
        token: confirmToken.trim(), password: deletePassword,
      });
      toast.success(`Account scheduled for deletion. Purge on ${r.data?.purge_at?.split("T")[0]}`);
      setShowDeleteModal(false);
      // The session is now invalid — bounce to the landing page after a beat.
      setTimeout(() => { window.location.href = "/"; }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirmation failed.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="dp-page" data-testid="data-privacy-page">
      <div className="dp-topbar">
        <Link to="/account" className="dp-back" data-testid="dp-back-link">← Back to Account</Link>
      </div>

      <header className="dp-hero">
        <div className="dp-eyebrow">Data &amp; Privacy</div>
        <h1 className="dp-title">You own your ministry data.</h1>
        <p className="dp-manifesto">
          Every Parish Profile, Worship &amp; Music Profile, SundayBuilder™ report, hymn selection, anthem plan,
          bulletin, and uploaded document you create in Liturgical Intelligence™ belongs to your parish, subject to
          third-party copyrights (such as hymnal publishers) and applicable law. You can export everything at any time
          without contacting support, and you can leave the platform whenever you choose — taking every artifact with
          you.
        </p>
        <p className="dp-manifesto-sub">
          <strong>AI &amp; content policy:</strong> Rasaan Hakiem, LLC does not use customer content to train
          proprietary AI models without your explicit consent. Authorized infrastructure providers may process content
          solely to provide the service under their applicable terms.
        </p>
      </header>

      {deletion.pending && (
        <div className="dp-banner dp-banner-warn" data-testid="dp-deletion-banner">
          <strong>Account deletion is in progress.</strong> Status:
          {" "}<code>{deletion.request?.status}</code>. If you did not request this, cancel from the email we sent
          you or contact support immediately.
        </div>
      )}

      <div className="dp-actions">
        <button className="dp-action dp-action-primary" onClick={() => createExport("all", "zip")}
                disabled={creating} data-testid="dp-download-everything">
          <div className="dp-action-icon">📦</div>
          <div className="dp-action-title">Download Everything</div>
          <div className="dp-action-sub">Full parish archive · ZIP · single click</div>
        </button>
        <a href="#export" className="dp-action" data-testid="dp-export-my-data">
          <div className="dp-action-icon">⬇️</div>
          <div className="dp-action-title">Export My Data</div>
          <div className="dp-action-sub">Choose specific data + format</div>
        </a>
        <a href="#correction" className="dp-action" data-testid="dp-request-correction">
          <div className="dp-action-icon">✎</div>
          <div className="dp-action-title">Request Data Correction</div>
          <div className="dp-action-sub">Ask us to correct or amend a record</div>
        </a>
        <button className="dp-action dp-action-danger" onClick={startDelete} data-testid="dp-delete-account">
          <div className="dp-action-icon">🗑</div>
          <div className="dp-action-title">Delete My Account</div>
          <div className="dp-action-sub">2-step confirmation · 30-day grace period</div>
        </button>
      </div>

      {/* Export section */}
      <section id="export" className="dp-section">
        <h2 className="dp-h2">Export My Data</h2>
        <div className="dp-export-controls">
          <div className="dp-field">
            <label>What to include</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}
                    data-testid="dp-scope-select">
              {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="dp-hint">{SCOPES.find((s) => s.key === scope)?.desc}</div>
          </div>
          <div className="dp-field">
            <label>Format</label>
            <select value={fmt} onChange={(e) => setFmt(e.target.value)}
                    data-testid="dp-format-select">
              {FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <button className="dp-btn dp-btn-primary" onClick={() => createExport()}
                  disabled={creating} data-testid="dp-create-export">
            {creating ? "Starting…" : "Create Export"}
          </button>
        </div>

        <h3 className="dp-h3">Recent exports</h3>
        <div className="dp-history">
          {history.length === 0 && <div className="dp-empty">No exports yet — create one above.</div>}
          {history.map((j) => {
            const live = statuses[j.id] || {};
            const status = live.status || j.status;
            const downloadable = status === "ready" && !j.downloaded_at;
            return (
              <div key={j.id} className="dp-history-row" data-testid={`dp-history-row-${j.id}`}>
                <div className="dp-history-main">
                  <div><strong>{j.scope}</strong> · {j.format}</div>
                  <div className="dp-history-meta">
                    Created {new Date(j.created_at).toLocaleString()} · <StatusPill status={status} />
                    {j.file_size && <> · {(j.file_size / 1024).toFixed(1)} KB</>}
                    {j.downloaded_at && <> · downloaded {new Date(j.downloaded_at).toLocaleString()}</>}
                  </div>
                </div>
                <div className="dp-history-actions">
                  {downloadable && (
                    <button className="dp-btn dp-btn-primary" onClick={() => downloadReady(j.id)}
                            data-testid={`dp-download-${j.id}`}>Download</button>
                  )}
                  {status === "pending" || status === "running" ? (
                    <button className="dp-btn" onClick={() => pollStatus(j.id)}
                            data-testid={`dp-poll-${j.id}`}>Refresh</button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Correction request section */}
      <section id="correction" className="dp-section">
        <h2 className="dp-h2">Request Data Correction</h2>
        <p className="dp-p">
          Ask us to correct or amend a specific record. We&apos;ll review and follow up by email.
        </p>
        <form className="dp-correction-form" onSubmit={submitCorrection}>
          <div className="dp-field">
            <label>Category</label>
            <select value={correction.category}
                    onChange={(e) => setCorrection({ ...correction, category: e.target.value })}
                    data-testid="dp-correction-category">
              <option value="parish_profile">Parish Profile</option>
              <option value="worship_music">Worship &amp; Music</option>
              <option value="report">A specific report</option>
              <option value="billing">Billing</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="dp-field">
            <label>Affected record ID (optional)</label>
            <input type="text" value={correction.affected_record_id}
                   onChange={(e) => setCorrection({ ...correction, affected_record_id: e.target.value })}
                   placeholder="e.g. report ID"
                   data-testid="dp-correction-record-id" />
          </div>
          <div className="dp-field dp-field-wide">
            <label>What needs to change?</label>
            <textarea rows="4" value={correction.description}
                      onChange={(e) => setCorrection({ ...correction, description: e.target.value })}
                      placeholder="Describe the incorrect information and what it should say."
                      data-testid="dp-correction-description" />
          </div>
          <button type="submit" className="dp-btn dp-btn-primary" disabled={submittingCorrection}
                  data-testid="dp-correction-submit">
            {submittingCorrection ? "Submitting…" : "Submit correction request"}
          </button>
        </form>
      </section>

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="dp-modal-backdrop" onClick={() => !confirming && setShowDeleteModal(false)}>
          <div className="dp-modal" onClick={(e) => e.stopPropagation()} data-testid="dp-delete-modal">
            <h2 className="dp-h2">Delete your account</h2>
            {deleteStep === 1 && (
              <>
                <p className="dp-p">This will start a 2-step deletion process:</p>
                <ul className="dp-list">
                  <li>Step 1 — Re-enter your password. We email you a confirmation token.</li>
                  <li>Step 2 — Paste the token to confirm. Sessions are revoked immediately.</li>
                  <li>You get a <strong>30-day grace period</strong> to cancel via the email we send.</li>
                  <li>After 30 days, all your parish data is permanently deleted.</li>
                </ul>
                <p className="dp-p dp-p-muted">
                  Records required for legal, tax, accounting, or fraud-prevention purposes (billing events, audit logs)
                  are retained per policy.
                </p>
                <p className="dp-p"><strong>Before continuing:</strong> did you download everything you need?</p>
                <div className="dp-modal-actions">
                  <button className="dp-btn" onClick={() => createExport("all", "zip")}
                          data-testid="dp-modal-download-first">Download everything first</button>
                  <button className="dp-btn dp-btn-danger" onClick={() => setDeleteStep(2)}
                          data-testid="dp-modal-continue">I understand — continue</button>
                  <button className="dp-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                </div>
              </>
            )}
            {deleteStep === 2 && (
              <>
                <div className="dp-field">
                  <label>Re-enter your password to authorize this request</label>
                  <input type="password" value={deletePassword}
                         onChange={(e) => setDeletePassword(e.target.value)}
                         autoComplete="current-password"
                         data-testid="dp-delete-password" />
                </div>
                <div className="dp-field">
                  <label>Why are you leaving? (optional)</label>
                  <input type="text" value={deleteReason}
                         onChange={(e) => setDeleteReason(e.target.value)}
                         data-testid="dp-delete-reason" />
                </div>
                <div className="dp-modal-actions">
                  <button className="dp-btn" onClick={() => setDeleteStep(1)}>Back</button>
                  <button className="dp-btn dp-btn-danger" onClick={requestDeletion}
                          disabled={!deletePassword || confirming}
                          data-testid="dp-request-delete">
                    {confirming ? "Sending…" : "Send confirmation email"}
                  </button>
                </div>
              </>
            )}
            {deleteStep === 3 && (
              <>
                <p className="dp-p">
                  Check your email for a confirmation token. Paste it below along with your password to complete
                  deletion. The token expires in 24 hours.
                </p>
                <div className="dp-field">
                  <label>Confirmation token from email</label>
                  <input type="text" value={confirmToken}
                         onChange={(e) => setConfirmToken(e.target.value)}
                         data-testid="dp-confirm-token" />
                </div>
                <div className="dp-field">
                  <label>Password</label>
                  <input type="password" value={deletePassword}
                         onChange={(e) => setDeletePassword(e.target.value)}
                         autoComplete="current-password"
                         data-testid="dp-confirm-password" />
                </div>
                <div className="dp-modal-actions">
                  <button className="dp-btn" onClick={() => setShowDeleteModal(false)}>Not now</button>
                  <button className="dp-btn dp-btn-danger" onClick={confirmDeletion}
                          disabled={!confirmToken || !deletePassword || confirming}
                          data-testid="dp-confirm-delete">
                    {confirming ? "Confirming…" : "Confirm deletion"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
